import { Fragment, useContext, useCallback, useMemo, useEffect } from 'react';
import { Area, LinePath } from '@visx/shape';
import DataContext from "../../../context/DataContext.js";
import getScaledValueFactory from "../../../utils/getScaledValueFactory.js";
import getScaleBaseline from "../../../utils/getScaleBaseline.js";
import isValidNumber from "../../../typeguards/isValidNumber.js";
import { AREASERIES_EVENT_SOURCE, XYCHART_EVENT_SOURCE } from "../../../constants.js";
import { BaseGlyphSeries } from "./BaseGlyphSeries.js";
import defaultRenderGlyph from "./defaultRenderGlyph.js";
import useSeriesEvents from "../../../hooks/useSeriesEvents.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
function BaseAreaSeries(_ref) {
  let {
    PathComponent = 'path',
    curve,
    data,
    dataKey,
    lineProps,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    enableEvents = true,
    renderLine = true,
    xAccessor,
    x0Accessor,
    xScale,
    yAccessor,
    y0Accessor,
    yScale,
    ...areaProps
  } = _ref;
  const {
    colorScale,
    theme,
    horizontal
  } = useContext(DataContext);
  const getScaledX0 = useMemo(() => x0Accessor ? getScaledValueFactory(xScale, x0Accessor) : undefined, [xScale, x0Accessor]);
  const getScaledX = useMemo(() => getScaledValueFactory(xScale, xAccessor), [xScale, xAccessor]);
  const getScaledY0 = useMemo(() => y0Accessor ? getScaledValueFactory(yScale, y0Accessor) : undefined, [yScale, y0Accessor]);
  const getScaledY = useMemo(() => getScaledValueFactory(yScale, yAccessor), [yScale, yAccessor]);
  const isDefined = useCallback(d => isValidNumber(xScale(xAccessor(d))) && isValidNumber(yScale(yAccessor(d))), [xScale, xAccessor, yScale, yAccessor]);
  const color = colorScale?.(dataKey) ?? theme?.colors?.[0] ?? '#222';
  const ownEventSourceKey = `${AREASERIES_EVENT_SOURCE}-${dataKey}`;
  const eventEmitters = useSeriesEvents({
    dataKey,
    enableEvents,
    onBlur,
    onFocus,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    source: ownEventSourceKey,
    allowedSources: [XYCHART_EVENT_SOURCE, ownEventSourceKey]
  });

  // accessor functions for the area generator
  const accessors = useMemo(() => {
    const numericScaleBaseline = getScaleBaseline(horizontal ? xScale : yScale);
    return horizontal ? {
      x0: getScaledX0 ?? numericScaleBaseline,
      x1: getScaledX,
      y: getScaledY
    } : {
      x: getScaledX,
      y0: getScaledY0 ?? numericScaleBaseline,
      y1: getScaledY
    };
  }, [xScale, yScale, horizontal, getScaledX, getScaledY, getScaledX0, getScaledY0]);

  // render invisible glyphs for focusing if onFocus/onBlur are defined
  const captureFocusEvents = Boolean(onFocus || onBlur);
  const renderGlyphs = useCallback(_ref2 => {
    let {
      glyphs
    } = _ref2;
    return captureFocusEvents ? glyphs.map(glyph => /*#__PURE__*/_jsx(Fragment, {
      children: defaultRenderGlyph({
        ...glyph,
        color: 'transparent',
        onFocus: eventEmitters.onFocus,
        onBlur: eventEmitters.onBlur
      })
    }, glyph.key)) : null;
  }, [captureFocusEvents, eventEmitters.onFocus, eventEmitters.onBlur]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Area, {
      ...accessors,
      ...areaProps,
      curve: curve,
      defined: isDefined,
      children: _ref3 => {
        let {
          path
        } = _ref3;
        return /*#__PURE__*/_jsx(PathComponent, {
          className: "visx-area",
          stroke: "transparent",
          fill: color,
          strokeLinecap: "round" // without this a datum surrounded by nulls will not be visible
          ,
          ...areaProps,
          d: path(data) || '',
          ...eventEmitters
        });
      }
    }), renderLine && /*#__PURE__*/_jsx(LinePath, {
      x: getScaledX,
      y: getScaledY,
      defined: isDefined,
      curve: curve,
      ...lineProps,
      children: _ref4 => {
        let {
          path
        } = _ref4;
        return /*#__PURE__*/_jsx(PathComponent, {
          className: "visx-line",
          fill: "transparent",
          stroke: color,
          strokeWidth: 2,
          pointerEvents: "none",
          strokeLinecap: "round" // without this a datum surrounded by nulls will not be visible
          ,
          ...lineProps,
          d: path(data) || ''
        });
      }
    }), captureFocusEvents && /*#__PURE__*/_jsx(BaseGlyphSeries, {
      dataKey: dataKey,
      data: data,
      xAccessor: xAccessor,
      yAccessor: yAccessor,
      xScale: xScale,
      yScale: yScale,
      renderGlyphs: renderGlyphs
    })]
  });
}
export default function BaseAreaSeriesWithRegisteredData(props) {
  const {
    dataKey,
    data,
    xAccessor,
    yAccessor
  } = props;
  const {
    xScale,
    yScale,
    dataRegistry
  } = useContext(DataContext);
  useEffect(() => {
    if (dataRegistry) dataRegistry.registerData({
      key: dataKey,
      data,
      xAccessor,
      yAccessor
    });
    return () => dataRegistry?.unregisterData(dataKey);
  }, [dataRegistry, dataKey, data, xAccessor, yAccessor]);
  const registryEntry = dataRegistry?.get(dataKey);

  // if scales or data are not available in context, render nothing
  if (!xScale || !yScale || !registryEntry) return null;

  // otherwise pass props + over-write data/accessors
  return /*#__PURE__*/_jsx(BaseAreaSeries, {
    ...props,
    xScale: xScale,
    yScale: yScale,
    data: registryEntry.data,
    xAccessor: registryEntry.xAccessor,
    yAccessor: registryEntry.yAccessor
  });
}